import { lazyWithRetry } from "@/react/lazyWithRetry";
import type { ComponentType } from "react";
import type { ZodTypeAny } from "zod";
import type { ActionDef } from "../core";
import { ClientActionPage } from "./ClientActionPage";

// ClientActionRegistry — maps an `ir.action` row's `client_component`
// identifier to the React component that mounts when the action is
// opened (action_type = "client").
//
// Enhancement 08 (Client-Action Lifecycle + Registry): the registry
// stores RICH DESCRIPTORS now (key + component + optional
// paramSchema + requires + lifecycle hooks) rather than bare
// components. Both registration shapes are supported:
//
//   registerClientAction("storage.drive", StorageDrive);   // legacy
//   registerClientAction({                                 // descriptor
//       key: "settings.general",
//       component: SettingsPage,
//       paramSchema: "uuid?",
//       requires: ["ir.role.admin"],
//       // Lifecycle hooks are registered at *mount* time by the
//       // component via `useClientActionLifecycle()` — the registry
//       // doesn't carry them statically because dirty state lives
//       // in component state.
//   });
//
// `ClientActionHost` (legacy export) still works; it just delegates
// to `<ClientActionPage>` with mountedAs="page" so any caller that
// hasn't migrated still gets the new permission check + Suspense.

/** Shape contract for the URL-derived params handed to the component.
    Two flavours, both validated at mount time by ClientActionPage:

    **(1) Legacy string discriminator** — single-slot validation:
      - `"uuid"`    : required uuid v4-shaped param
      - `"uuid?"`   : optional uuid; empty allowed
      - `"any"`     : no validation (or omit entirely)

      Simplest. Components receive `{ params: { param: "<uuid>" } }`.

    **(2) Zod schema** — full object-shape validation. The schema
    validates the merged URL params:

        {
          ...querystring,              // from window.location.search
          param: "<recordRef>"         // the trailing record-uuid slot
        }

    Use when the action takes multiple typed fields, enums, numeric
    ranges, or conditional rules. The validated/parsed `z.infer<...>`
    result is handed to the component as `params` — kwargs-ready, so
    consumers thread it straight into command dispatches without
    re-parsing the URL each time. Default values, coercions, and
    transforms in the schema all apply.

    Example — `approval.case` opening on a specific case + tab:

        const schema = z.object({
            param: z.string().uuid(),
            tab: z.enum(["activity", "comments", "files"]).default("activity"),
            line: z.coerce.number().int().positive().optional(),
        });
        registerClientAction({ key: "approval.case", component, paramSchema: schema });

        // URL: /wc/myorg/approval-case/form/<uuid>?tab=comments&line=3
        // → component receives params = { param, tab: "comments", line: 3 }

    On validation failure the host renders "Bad URL parameter for <key>:
    <field>: <reason>" enumerating every issue — admins (or users) see
    the typo at click time instead of a silent fail. */
export type ClientActionParamSchema = "uuid" | "uuid?" | "any" | ZodTypeAny;

/** Built by ClientActionPage from `param` (trailing record-uuid URL
    segment) + URL query string. For the legacy string-discriminator
    schemas this is always `{ param: "..." }`. For zod schemas this is
    the fully parsed `z.infer<typeof schema>`. Components read whichever
    fields their schema declared — TS narrows automatically when the
    descriptor's schema type is preserved at the call site. */
export type ClientActionParams = Record<string, unknown>;

export interface ClientActionProps {
    action: ActionDef;
    /** Legacy: trailing URL record-uuid segment as a raw string.
        Empty / undefined for menu-only actions. Preserved for backward
        compat with consumers built before `params` existed. */
    param?: string;
    /** Validated + parsed URL params, ready to thread into command
        dispatches as kwargs. For zod-schema actions this is the
        `z.infer<...>` result. For legacy string-discriminator actions
        this is `{ param: "..." }` (or `{}` when no param). */
    params: ClientActionParams;
}

export type ClientComponent = ComponentType<ClientActionProps>;

export interface ClientActionDescriptor {
    /** Must exactly match the `ir.action.client_component` field of
        the registered `ir.action` row. */
    key: string;
    /** The React component to mount. May be `lazy()`-wrapped. */
    component: ClientComponent;
    /** Optional URL-param shape contract — checked at mount time. A
        `"uuid"` declaration with no param (or a non-uuid-shaped param)
        renders the parameter-mismatch fallback instead of mounting. */
    paramSchema?: ClientActionParamSchema;
    /** Optional role gate. Today's `UserIdentity` doesn't yet carry
        roles; the field is forward-compat — when roles land in the
        session payload, ClientActionPage will enforce this at mount. */
    requires?: string[];
    /** Optional descriptor-level setup hook. Fires once before the
        component mounts (after registry + param + permission checks).
        Use to load drafts from localStorage, fetch prerequisites, or
        abort the open by throwing. Render is gated on its completion
        — a "Loading…" placeholder shows while pending. Throwing
        surfaces as the "Failed to open" placeholder.

        Returns `void`. For dynamic per-mount state (dirty veto, save
        callbacks) use `useClientActionLifecycle()` inside the
        component instead — this hook is for static, descriptor-bound
        setup that doesn't depend on component state. */
    onOpen?: (ctx: { action: import("../core").ActionDef; param?: string; params: ClientActionParams }) => Promise<void> | void;
    /** Optional layout-XML reference (Enhancement 08 layout slice).
        When set, ClientActionPage fetches the parsed `<ClientActionView>`
        plan via `loadModelView(layout.modelKey, "clientaction")` and
        wraps the component inside the layout's chrome (header / body /
        footer). The descriptor's React component mounts in the layout's
        `<component/>` slot; imperative DOM handles for plugin-style
        components arrive via the `useClientActionLayoutRefs()` hook
        (TODO when first plugin adopter lands).

        Shape: `{ modelKey, viewId? }` — modelKey is required; the
        loader looks up the model's registered clientaction view. The
        optional `viewId` is for future "pick a specific clientaction
        view among many on the same model" cases. */
    layout?: { modelKey: string; viewId?: string };
}

/** Type guard — true when the schema is a zod schema (vs a legacy
    string discriminator). Detection via the well-known `_def` slot
    every zod schema carries; cheap, no import-time work. */
export function isZodSchema(schema: ClientActionParamSchema | undefined): schema is ZodTypeAny {
    return typeof schema === "object" && schema !== null && "_def" in schema && typeof (schema as { parse?: unknown }).parse === "function";
}

interface _RegistryEntry {
    descriptor: ClientActionDescriptor;
    /** Source file path attached at registration time. Used by the
        collision-detection error so a duplicate-key registration tells
        the developer both file paths to fix. Glob discovery passes the
        glob's resolved path; manual `registerClientAction(...)` calls
        get `"<inline>"`. */
    sourcePath: string;
}

const REGISTRY: Record<string, _RegistryEntry> = {};

// Settings — first frontend2 adopter (Slice 1 of the Settings client-
// action port). Lazy-loaded so the bundle stays slim for users who
// never open Settings.
const SettingsPage = lazyWithRetry(() => import("../settings/SettingsPage").then((m) => ({ default: m.SettingsPage })), "settings.general");

REGISTRY["settings.general"] = {
    descriptor: {
        key: "settings.general",
        component: SettingsPage,
        paramSchema: "any",
    },
    sourcePath: "<bundled:settings.general>",
};

// Enhancement 08 Item #4 — admin debug page listing every registered
// client_component key + the ir.action rows that reference each. Gated
// to `ir.role.admin` so non-admins don't see internal plumbing surface.
const ClientActionsDebugPage = lazyWithRetry(
    () => import("../developer/ClientActionsDebugPage").then((m) => ({ default: m.ClientActionsDebugPage })),
    "developer.client_actions",
);

REGISTRY["developer.client_actions"] = {
    descriptor: {
        key: "developer.client_actions",
        component: ClientActionsDebugPage,
        paramSchema: "any",
        requires: ["ir.role.admin"],
    },
    sourcePath: "<bundled:developer.client_actions>",
};

/** Add a client component to the registry at runtime (consumer modules
    register their own client actions during boot). Two signatures:

    1. `registerClientAction("key", Component)` — legacy / shorthand.
       Wraps the component in a minimal descriptor (no paramSchema,
       no requires, no lifecycle).
    2. `registerClientAction({ key, component, ... })` — full descriptor
       with optional paramSchema + requires + future lifecycle metadata.

    Re-registering an existing key REPLACES the descriptor (latest boot
    order wins). For boot-time collision detection — two modules
    registering the SAME command from different files — use
    `registerClientActionFromGlob(...)` which the glob-discovery module
    calls; it raises a clear error naming both source paths.

    The string `key` must match the `ir.action.client_component` field
    of the registered `ir.action` row exactly. */
export function registerClientAction(key: string, component: ClientComponent): void;
export function registerClientAction(descriptor: ClientActionDescriptor): void;
export function registerClientAction(
    keyOrDescriptor: string | ClientActionDescriptor,
    component?: ClientComponent,
): void {
    if (typeof keyOrDescriptor === "string") {
        if (!component) {
            throw new Error(
                `registerClientAction("${keyOrDescriptor}", …) called without a component — `
                + `pass the React component as the second argument.`,
            );
        }
        REGISTRY[keyOrDescriptor] = {
            descriptor: { key: keyOrDescriptor, component },
            sourcePath: "<inline>",
        };
        return;
    }
    REGISTRY[keyOrDescriptor.key] = {
        descriptor: keyOrDescriptor,
        sourcePath: "<inline>",
    };
}

/** Glob-discovery entry point — raises on duplicate-key collision
    rather than replacing. The error names BOTH source paths so the
    developer sees exactly which two files to fix. Same loud-fail
    discipline as the model registry's duplicate-key check.

    `sourcePath` is the glob's resolved file path (e.g.
    `/src/ede/foundation/storage/frontend/client-actions/drive.tsx`)
    — the discovery module passes it from `import.meta.glob`'s keys
    so the error message is concretely actionable. */
export function registerClientActionFromGlob(
    descriptor: ClientActionDescriptor,
    sourcePath: string,
): void {
    const existing = REGISTRY[descriptor.key];
    if (existing && !existing.sourcePath.startsWith("<")) {
        // Two real source paths colliding — refuse to silently shadow.
        throw new Error(
            `[clientActions] Duplicate command "${descriptor.key}":\n`
            + `  first registered:  ${existing.sourcePath}\n`
            + `  duplicate found:   ${sourcePath}\n`
            + `Two modules cannot register the same command. Rename one.`,
        );
    }
    REGISTRY[descriptor.key] = { descriptor, sourcePath };
}

/** Look up a descriptor by key. Returns `null` when the key isn't
    registered — callers (ClientActionPage) render the missing-key
    fallback. */
export function getClientActionDescriptor(key: string): ClientActionDescriptor | null {
    return REGISTRY[key]?.descriptor ?? null;
}

/** Snapshot of every registered key — used by the admin debug tool
    and by tests. Not for hot-path consumption; copies the keys array. */
export function listRegisteredClientActions(): string[] {
    return Object.keys(REGISTRY).slice().sort();
}

/** Snapshot of `(key → sourcePath)` for the admin debug page — admins
    can see WHERE each client action was registered from. */
export function getClientActionSourcePath(key: string): string | null {
    return REGISTRY[key]?.sourcePath ?? null;
}

/** Legacy thin shim — delegates to ClientActionPage in `page` mode.
    Kept exported so any caller that hasn't migrated to
    <ClientActionPage> still gets the new permission check, Suspense,
    and lifecycle plumbing under the hood. */
export function ClientActionHost({ action, param }: ClientActionProps) {
    return (
        <ClientActionPage
            action={action}
            param={param}
            mountedAs="page"
        />
    );
}
